import { useModals } from 'almanac-ui/Modal'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { RegenerateDesignModal } from 'components/RegenerateDesignModal/RegenerateDesignModal'
import { ImageMenuModal } from 'components/ImageMenu/ImageMenuModal/ImageMenuModal'
import { ConfirmReplaceDesignModal } from '../EditPostMenu/ConfirmReplaceDesignModal'
import { useReplaceFrameContent } from 'hooks/queries/designs/useReplaceFrameContent'
import { useReplaceVideoContent } from 'hooks/queries/videos/useReplaceVideoContent'
import { usePollDesignReplacement } from '../EditPostMenu/usePollDesignReplacement'
import { useCurrentSlide } from '../CurrentSlideContext'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { Upload5 } from 'blaze-ui/icons/20/Upload5'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { isVideoChannel } from '../utils'
import { DesignChannel } from 'components/NewFileModal/Types'
import { ImageMenuSelectedImage, Provider } from 'components/ImageMenu/ImageMenuModal/ImageMenuContext'
import {
  ACCEPTED_IMAGE_AND_VIDEO_MIME_TYPES_NO_SVG,
  ACCEPTED_VIDEO_MIME_TYPES,
} from 'screens/Finder/components/views/GalleryView/constants'
import { useState } from 'react'
import { Text } from 'blaze-ui/Text/Text'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { isUgcScriptVideoContent } from 'utils/videoScriptSettings'
import Styles from './Sidebar.module.scss'
import { ArrowRotateRight } from 'blaze-ui/icons/20'

interface RedesignSectionProps {
  almanacFile: AlmanacFileShowItem
  isInsertingMedia?: boolean
  onLoadingChange?: (isLoading: boolean) => void
}

export const RedesignSection = observer(
  ({ almanacFile, isInsertingMedia = false, onLoadingChange }: RedesignSectionProps) => {
    const [selectedMedia, setSelectedMedia] = useState<ImageMenuSelectedImage>()
    const { generationTimestampStore } = useRootStore()
    const { currentSlideIndex } = useCurrentSlide()
    const { openModal, closeStack } = useModals()

    const isVideo = almanacFile?.channel ? isVideoChannel(almanacFile.channel as DesignChannel) : false
    const isVideoAsset = almanacFile.asset_type === 'Video'
    const replaceFrameBatchId = generationTimestampStore.getReplaceFrameBatchId(almanacFile.key)
    const isReplacingFrame = !!replaceFrameBatchId
    const isGenerating = almanacFile.is_generating || isReplacingFrame
    const isBusy = isGenerating || isInsertingMedia

    const isAvatarVideo = almanacFile.content_type === 'avatar_video'
    const showVideoVoiceoverCaptions = useFlipperFlag('video_voiceover_captions')
    const isUgcScriptVideo = showVideoVoiceoverCaptions && isUgcScriptVideoContent(almanacFile)
    let regenerateLabel = 'Regenerate Design'
    let regenerateSubtitle = 'Blaze will generate new design'
    if (isAvatarVideo || isUgcScriptVideo) {
      regenerateLabel = 'Regenerate video'
      regenerateSubtitle = 'Edit script and style before generating'
    } else if (isVideo || isVideoAsset) {
      regenerateLabel = 'Regenerate Video'
      regenerateSubtitle = 'Blaze will generate new video'
    }
    const replaceLabel = isVideo || isVideoAsset ? 'Replace with Video' : 'Replace with Media'
    const replaceSubtitle = isVideo || isVideoAsset ? 'Swap video with your own' : 'Swap design with your own'
    let replaceTitle = 'Replace this design with your own image or video'
    if (isVideoAsset) {
      replaceTitle = 'Replace this video with your own'
    } else if (isVideo) {
      replaceTitle = 'Replace this design with your own video'
    }
    const accept = isVideo || isVideoAsset ? ACCEPTED_VIDEO_MIME_TYPES : ACCEPTED_IMAGE_AND_VIDEO_MIME_TYPES_NO_SVG
    const hiddenProviders: Provider[] =
      isVideo || isVideoAsset
        ? ['gifs', 'blazeRecommendations', 'stockPhoto', 'hardcodedAi']
        : ['gifs', 'blazeRecommendations']

    const replaceMutationOptions = {
      onMutate: () => onLoadingChange?.(true),
      onSuccess: (data: { batch_id: string }) => {
        generationTimestampStore.startGeneration(
          almanacFile.key,
          'default',
          isVideoAsset ? 'Replacing video...' : 'Replacing design...',
          selectedMedia?.thumbnail_url || selectedMedia?.url,
          data.batch_id,
        )
      },
      onError: () => {
        onLoadingChange?.(false)
        generationTimestampStore.completeGeneration(almanacFile.key)
        showToastMessage(isVideoAsset ? 'Failed to replace video' : 'Failed to replace design', 'error')
      },
    }

    const { mutate: replaceFrameContent } = useReplaceFrameContent(
      { designId: almanacFile.key },
      replaceMutationOptions,
    )

    const { mutate: replaceVideoContent } = useReplaceVideoContent(
      { videoId: almanacFile.key },
      replaceMutationOptions,
    )

    usePollDesignReplacement({
      replaceFrameBatchId,
      designKey: almanacFile.key,
      onComplete: () => {
        generationTimestampStore.completeGeneration(almanacFile.key)
        onLoadingChange?.(false)
      },
      scheduledPostId: almanacFile?.scheduled_post?.id,
      failureMessage: isVideoAsset ? 'Failed to replace video' : 'Failed to replace design',
    })

    const handleRegenerate = () => {
      openModal(RegenerateDesignModal, { almanacFile }, { newStack: true })
    }

    const handleReplace = () => {
      openModal(
        ImageMenuModal,
        {
          title: replaceTitle,
          hiddenProviders,
          accept,
          uploadMedia: true,
          maxCount: 1,
          closeOnUse: false,
          onUse: (images: ImageMenuSelectedImage[]) => {
            const media = images[0]
            setSelectedMedia(media)
            openModal(ConfirmReplaceDesignModal, {
              title: replaceTitle,
              mediaUrl: media.url,
              mediaMimeType: media.mime_type || '',
              description: isVideoAsset
                ? 'Your selection will replace the current video'
                : 'Your selection will replace the current design',
              confirmLabel: isVideoAsset ? 'Apply as Video' : 'Apply as Design',
              onConfirm: () => {
                if (!media?.id) return
                if (isVideoAsset) {
                  replaceVideoContent({ media_file_key: media.id })
                } else {
                  replaceFrameContent({
                    frames: [
                      {
                        frame_position: currentSlideIndex + 1,
                        media_file_key: media.id,
                      },
                    ],
                  })
                }
                closeStack()
              },
            })
          },
        },
        { newStack: true },
      )
    }

    return (
      <>
        <div className={Styles.sectionLabel}>
          <Text variant="placeholder" color="var(--dark-40)">
            Redesign
          </Text>
        </div>
        <div>
          <button className={Styles.redesignButton} disabled={isBusy} onClick={handleRegenerate}>
            <ArrowRotateRight size={15} />
            <div className={Styles.redesignButtonText}>
              <Text variant="secondary" color="var(--dark-90)">
                {regenerateLabel}
              </Text>
              <Text variant="metadata" color="var(--dark-60)">
                {regenerateSubtitle}
              </Text>
            </div>
          </button>

          <button className={Styles.redesignButton} disabled={isBusy} onClick={handleReplace}>
            <Upload5 size={15} />
            <div className={Styles.redesignButtonText}>
              <Text variant="secondary" color="var(--dark-90)">
                {replaceLabel}
              </Text>
              <Text variant="metadata" color="var(--dark-60)">
                {replaceSubtitle}
              </Text>
            </div>
          </button>
        </div>
      </>
    )
  },
)
